import { useErrorNotification } from '@providers/ErrorsNotificationProvider';
import {
  QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

import { MemberAux } from '@features/club/typings';
import { components, DELETE, GET, POST } from '@features/shared/api/club/clients';
import { ClockControlType, MemberDto, NewsPost } from '@features/shared/api/typings';

const QUERY_KEYS = {
  infiniteClubEvents({
    url,
    clubId,
    isManager,
    type,
    query,
    tickets,
    rcfRankCalculated,
  }: {
    url: string;
    clubId: string;
    isManager?: boolean;
    type: 'upcoming' | 'finished';
    query?: string;
    tickets?: string[];
    rcfRankCalculated?: boolean;
  }) {
    return [
      'INFINITE_EVENTS',
      url,
      clubId,
      isManager,
      type,
      query,
      tickets?.join('//'),
      rcfRankCalculated,
    ];
  },
};

export function useClubCardQuery(
  clubId?: string,
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    queryKey: ['/api/v1/user/clubs/{clubId}', clubId],
    queryFn: async () => {
      if (!clubId) return;
      return (
        await GET('/api/v1/user/clubs/{clubId}', {
          params: {
            path: {
              clubId,
            },
          },
        })
      ).data!;
    },
  });
}

export function useClubMembersQuery(
  clubId: string,
  options: {
    sortBy: 'Alphabet' | 'RatingBlitz' | 'RatingRapid' | 'RatingClassic';
    status?: 'JoinRequest' | 'Active' | 'Deleted' | 'Invited';
    isManager?: boolean;
    query?: string;
  },
) {
  const { sortBy, status, isManager } = options;

  const url = isManager
    ? '/api/v1/manager/clubs/{clubId}/members'
    : '/api/v1/user/clubs/{clubId}/members';

  const result = useInfiniteQuery({
    queryKey: [url, clubId, sortBy, status, isManager],
    getNextPageParam: (lastPage: { nextCursor?: number | null }) => lastPage.nextCursor,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await GET(url, {
          params: {
            path: {
              clubId,
            },
            query: {
              sortBy,
              status,
              cursor: pageParam ? Number(pageParam) : 0,
              limit: 500,
            },
          },
        })
      ).data!,
  });

  return {
    ...result,
    data: result?.data?.pages?.reduce(
      // TODO fix, after BE adds participationStatus
      (res: MemberDto[], p) => res.concat(p.result as unknown as MemberDto[]),
      [],
    ),
  };
}

export function useManagerClubMembersQuery(
  clubId: string,
  options: {
    sortBy: 'Alphabet' | 'RatingBlitz' | 'RatingRapid' | 'RatingClassic';
    status?: 'JoinRequest' | 'Active' | 'Deleted' | 'Invited';
    query?: string;
  },
) {
  const { sortBy, status, query } = options;

  const result = useInfiniteQuery({
    queryKey: ['/api/v1/manager/clubs/{clubId}/members', clubId, sortBy, status],
    getNextPageParam: (lastPage: { nextCursor?: number | null }) => lastPage.nextCursor,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await GET('/api/v1/manager/clubs/{clubId}/members', {
          params: {
            path: {
              clubId,
            },
            query: {
              sortBy,
              status: status
                ? status === 'Deleted'
                  ? ['Deleted', 'JoinRequestRejected', 'InviteRejected', 'Leaved']
                  : [status]
                : undefined,
              cursor: pageParam ? Number(pageParam) : 0,
              query,
              limit: 500,
            },
          },
        })
      ).data!,
  });

  return {
    ...result,
    data: result?.data?.pages?.reduce(
      // TODO fix, after BE adds participationStatus
      (res: MemberDto[], p) => res.concat(p.result as unknown as MemberDto[]),
      [],
    ),
  };
}

export type IResetClubEventQueryArg = {
  qc: QueryClient;
  clubId: string;
  type: 'upcoming' | 'finished';
  isManager?: boolean;
  query?: string;
  tickets?: string[];
  rcfRankCalculated?: boolean;
};
export function resetClubEventsQuery({
  qc,
  clubId,
  type,
  isManager,
  query,
  tickets,
  rcfRankCalculated,
}: IResetClubEventQueryArg) {
  const url = getClubEventsUrl(type, isManager);

  const queryKey = QUERY_KEYS.infiniteClubEvents({
    url,
    clubId,
    isManager,
    type,
    query,
    tickets,
    rcfRankCalculated,
  });

  qc.setQueryData(queryKey, (oldData?: { pages: unknown[]; pageParams: unknown[] }) => {
    if (!oldData) return undefined;
    return {
      pages: oldData.pages.slice(0, 1),
      pageParams: oldData.pageParams.slice(0, 1),
    };
  });
  // TODO this does a duplicate query. Figure out another solution.
  // @see https://github.com/TanStack/query/discussions/3576
  qc.invalidateQueries({ queryKey });
}

function getClubEventsUrl(type: 'upcoming' | 'finished', isManager?: boolean) {
  return isManager
    ? type === 'upcoming'
      ? '/api/v1/manager/clubs/{clubId}/tournaments/upcoming'
      : '/api/v1/manager/clubs/{clubId}/tournaments/finished'
    : type === 'upcoming'
      ? '/api/v1/user/clubs/{clubId}/tournaments/upcoming'
      : '/api/v1/user/clubs/{clubId}/tournaments/finished';
}

type ClubEventsQueryParams = {
  clubId: string;
  type: 'upcoming' | 'finished';
  isManager?: boolean;
  query?: string;
  tickets?: string[];
  pageSize?: number;
  enabled?: boolean;
  rcfRankCalculated?: boolean;
};
type Event = components['schemas']['TournamentShortDto'];

// Тут два хука, для infinite-scroll и постраничной подгрузки
export function useInfiniteClubEventsQuery(opts: ClubEventsQueryParams) {
  const {
    clubId,
    type,
    isManager,
    query,
    enabled,
    tickets,
    pageSize = 16,
    rcfRankCalculated,
  } = opts;
  const url = getClubEventsUrl(type, isManager);

  const result = useInfiniteQuery({
    enabled,
    getNextPageParam: (lastPage: Array<Event>, _allPages, lastPageParam) => {
      if (type === 'upcoming' || lastPage.length < pageSize) return;
      return lastPageParam + 1;
    },
    initialPageParam: 0,
    queryKey: QUERY_KEYS.infiniteClubEvents({
      url,
      clubId,
      isManager,
      type,
      query,
      tickets,
      rcfRankCalculated,
    }),
    queryFn: async ({ pageParam }) => {
      let paging;

      if (type === 'finished') {
        paging = {
          limit: pageSize,
          offset: pageSize * pageParam,
        };
      }

      return (
        await GET(url, {
          params: {
            query: {
              query,
              tickets,
              rcfRankCalculated,
              ...paging,
            },
            path: {
              clubId,
            },
          },
        })
      ).data!;
    },
  });

  // Собираем все страницы в кучу
  let data: Event[] = [];
  result?.data?.pages?.forEach((page) => {
    data = data.concat(page);
  });

  return { ...result, data };
}

export function useMemberGroupsQuery(clubId: string, enabled: boolean) {
  return useQuery({
    enabled,
    queryKey: ['/api/v1/user/clubs/{clubId}/members/groups', clubId],
    queryFn: async () =>
      (
        await GET('/api/v1/manager/clubs/{clubId}/members/groups', {
          params: {
            path: {
              clubId,
            },
          },
        })
      ).data!,
  });
}

export function useNewsQuery(clubId: string, limit: number) {
  const result = useInfiniteQuery({
    queryKey: ['/api/v1/user/clubs/{clubId}/news-posts', clubId],
    getNextPageParam: (lastPage: NewsPost[], _, lastPageParam) =>
      lastPage.length >= limit ? lastPageParam + 1 : undefined,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await GET('/api/v1/user/clubs/{clubId}/news-posts', {
          params: {
            path: {
              clubId,
            },
            query: {
              limit,
              offset: limit * pageParam,
            },
          },
        })
      ).data!,
  });

  let data: NewsPost[] = [];
  result?.data?.pages.forEach((page) => {
    data = data.concat(page);
  });

  return {
    ...result,
    data,
  };
}

export function useMyStatsQuery(clubId: string, { enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    enabled,
    queryKey: ['/api/v1/user/clubs/{clubId}/stat', clubId],
    queryFn: async () =>
      (
        await GET('/api/v1/user/clubs/{clubId}/stat', {
          params: {
            path: {
              clubId,
            },
          },
        })
      ).data!,
  });
}

export function useTopPlayersQuery(
  clubId: string,
  clockControlType: ClockControlType,
  query?: string,
) {
  const LIMIT = 50;
  const result = useInfiniteQuery({
    queryKey: ['/api/v1/user/clubs/{clubId}/top-players', clubId, clockControlType, query],
    getNextPageParam: (lastPage: components['schemas']['TopPlayersResultDto'], _, lastPageParam) =>
      lastPage.topPlayers.length >= LIMIT ? lastPageParam + 1 : undefined,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await GET('/api/v1/user/clubs/{clubId}/top-players', {
          params: {
            path: {
              clubId,
            },
            query: {
              clockControlType,
              includeSelf: true,
              globalRanking: false,
              query,
              limit: LIMIT,
              offset: LIMIT * pageParam,
            },
          },
        })
      ).data!,
  });

  const data: components['schemas']['TopPlayersResultDto'] = {
    topPlayers: [],
    self: result?.data?.pages[0]?.self,
  };
  result?.data?.pages.forEach((page) => {
    data.topPlayers = data.topPlayers.concat(page.topPlayers);
  });

  return {
    ...result,
    data,
  };
}

export function useWinnersQuery(clubId: string) {
  const LIMIT = 50;
  const result = useInfiniteQuery({
    queryKey: ['/api/v1/user/clubs/{clubId}/tournament-winners', clubId],
    getNextPageParam: (
      lastPage: components['schemas']['TournamentWinnerDto'][],
      _,
      lastPageParam,
    ) => (lastPage.length >= LIMIT ? lastPageParam + 1 : undefined),
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (
        await GET('/api/v1/user/clubs/{clubId}/tournament-winners', {
          params: {
            path: {
              clubId,
            },
            query: {
              limit: LIMIT,
              offset: LIMIT * pageParam,
            },
          },
        })
      ).data!,
  });

  let data: components['schemas']['TournamentWinnerDto'][] = [];
  result?.data?.pages.forEach((page) => {
    data = data.concat(page);
  });

  return {
    ...result,
    data,
  };
}

export function useJoinClubMutation(clubId: string) {
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/user/clubs/{clubId}/membership/actions/join', {
          params: { path: { clubId } },
        })
      ).data!;
    },
  });
}

export function useLeaveClubMutation(clubId: string) {
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/user/clubs/{clubId}/membership/actions/leave', {
          params: { path: { clubId } },
        })
      ).data!;
    },
  });
}

export function useAcceptClubRulesMutation(clubId: string) {
  const queryClient = useQueryClient();
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async () => {
      return (
        await POST('/api/v1/user/clubs/{clubId}/membership/actions/accept-rules', {
          params: { path: { clubId } },
        })
      ).data!;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['/api/v1/user/clubs/{clubId}', clubId], data);
    },
    onError: report,
  });
}

export function useActivateClubMemberMutation(clubId: string) {
  return useMutation({
    mutationFn: async (userId: string) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/members/{userId}/actions/activate', {
          params: { path: { clubId, userId } },
        })
      ).data!;
    },
  });
}

export function useRejectClubMemberMutation(clubId: string) {
  return useMutation({
    mutationFn: async (userId: string) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/members/{userId}/actions/reject', {
          params: { path: { clubId, userId } },
        })
      ).data!;
    },
  });
}

export function useInviteClubMemberMutation(clubId: string) {
  return useMutation({
    mutationFn: async (userId: string) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/members/{userId}/actions/invite', {
          params: { path: { clubId, userId } },
        })
      ).data!;
    },
  });
}

export function useDeleteClubMemberMutation(clubId: string) {
  return useMutation({
    mutationFn: async (userId: string) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/members/{userId}/actions/delete', {
          params: {
            path: { clubId, userId },
          },
        })
      ).data!;
    },
  });
}

export function useUpdateClubMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async (club: components['schemas']['UpdateClubRequest']) => {
      if (!club.themeColor) {
        club.themeColor = null;
      }

      return (
        await POST('/api/v1/manager/clubs/{clubId}', {
          params: {
            path: {
              clubId,
            },
          },
          body: club,
        })
      ).data!;
    },
    onError: report,
  });
}

export function useUpdateMemberGroupMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ userId, groupName }: { userId: string; groupName: string | null }) => {
      return (
        await POST('/api/v1/manager/clubs/{clubId}/members/{userId}/group', {
          params: {
            path: {
              clubId,
              userId,
            },
          },
          body: { groupName },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useSaveNewsPostMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async (newsPost: NewsPost) => {
      if (newsPost.id) {
        return (
          await POST('/api/v1/manager/clubs/{clubId}/news-posts/{newsPostId}', {
            params: {
              path: {
                clubId,
                newsPostId: newsPost.id,
              },
            },
            body: newsPost,
          })
        ).data!;
      }

      return (
        await POST('/api/v1/manager/clubs/{clubId}/news-posts', {
          params: {
            path: {
              clubId,
            },
          },
          body: newsPost,
        })
      ).data!;
    },
    onError: report,
  });
}

export function useDeleteNewsPostMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async (newsPostId: string) => {
      return (
        await DELETE('/api/v1/manager/clubs/{clubId}/news-posts/{newsPostId}', {
          params: {
            path: {
              clubId,
              newsPostId,
            },
          },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useClubUploadLogoMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationKey: ['clubUploadSponsorLogo', clubId],
    mutationFn: async (file: FormData) => {
      const result = await POST(`/api/v1/manager/clubs/{clubId}/logo`, {
        params: { path: { clubId } },
        // TODO fix openapi generation
        body: file as unknown as never,
      });
      if (!result.response.ok) {
        throw result.error;
      }
      return result;
    },
    onError: report,
  });
}

export function useClubUploadSponsorLogoMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationKey: ['clubUploadLogo', clubId],
    mutationFn: async ({
      sponsorNumber,
      formData,
    }: {
      sponsorNumber: number;
      formData: FormData;
    }) => {
      const result = await POST(`/api/v1/manager/clubs/{clubId}/sponsors/{sponsorNumber}/logo`, {
        params: { path: { clubId, sponsorNumber } },
        // TODO fix openapi generation
        body: formData as unknown as never,
      });
      if (!result.response.ok) {
        throw result.error;
      }
      return result;
    },
    onError: report,
  });
}

export function useAdminCreateClubMutation() {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async (query: { clubId: string; title: string }) => {
      return (
        await POST('/api/v1/admin/clubs', {
          params: {
            query,
          },
        })
      ).data!;
    },
    onError: report,
  });
}

export function useAdminUpdateClubMutation(clubId: string) {
  const { report } = useErrorNotification();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (club: components['schemas']['AdminUpdateClubRequest']) => {
      if (!club.themeColor) {
        club.themeColor = null;
      }

      return (
        await POST('/api/v1/admin/clubs/{clubId}', {
          params: {
            path: {
              clubId,
            },
          },
          body: club,
        })
      ).data!;
    },
    onError: report,
    onSuccess: () => {
      return qc.invalidateQueries({ queryKey: ['/api/v1/user/clubs/{clubId}', clubId] });
    },
  });
}

export function useAdminClubManagersQuery(
  clubId?: string,
  { enabled = true, staleTime }: { enabled?: boolean; staleTime?: number } = {},
) {
  return useQuery({
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    queryKey: ['/api/v1/admin/clubs/{clubId}/managers', clubId],
    queryFn: async () => {
      if (!clubId) return;
      return (
        await GET('/api/v1/admin/clubs/{clubId}/managers', {
          params: {
            path: {
              clubId,
            },
          },
        })
      ).data!;
    },
  });
}

export function useAdminDeleteClubManagerMutation(clubId: string) {
  const { report } = useErrorNotification();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      return (
        await DELETE('/api/v1/admin/clubs/{clubId}/managers/{email}', {
          params: {
            path: {
              clubId,
              email,
            },
          },
        })
      ).data!;
    },
    onError: report,
    onSuccess: () => {
      return Promise.all([
        qc.invalidateQueries({ queryKey: ['/api/v1/user/me'] }),
        qc.invalidateQueries({ queryKey: ['/api/v1/admin/clubs/{clubId}/managers', clubId] }),
      ]);
    },
  });
}

export function useAdminPostClubManagerMutation(clubId: string) {
  const { report } = useErrorNotification();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      return (
        await POST('/api/v1/admin/clubs/{clubId}/managers/{email}', {
          params: {
            path: {
              clubId,
              email,
            },
          },
        })
      ).data!;
    },
    onError: report,
    onSuccess: () => {
      return Promise.all([
        qc.invalidateQueries({ queryKey: ['/api/v1/user/me'] }),
        qc.invalidateQueries({ queryKey: ['/api/v1/admin/clubs/{clubId}/managers', clubId] }),
      ]);
    },
  });
}

// aux участника клуба: настройки игрока на уровне клуба (число камер на видеоконтроле)
export function useUpdateClubMemberAuxMutation(clubId: string) {
  const { report } = useErrorNotification();
  return useMutation({
    mutationFn: async ({ userId, aux }: { userId: string; aux: MemberAux | null }) => {
      await POST('/api/v1/manager/clubs/{clubId}/members/{userId}/aux', {
        params: { path: { clubId, userId } },
        body: { aux },
      });
    },
    onError: report,
  });
}
